/**
 * diffPayload.js — structural diff between two canvas payloads.
 *
 * Canvas V2 version-diff view (#170 / Tier-2). Given a `base` payload
 * (the "before" revision) and a `current` payload (the "after"), compute:
 *   - added:    ids present in current but not in base
 *   - removed:  ids present in base but not in current
 *   - modified: ids present in both, with a DIFFERENT structural shape
 *               (label OR config JSON — ignoring purely cosmetic hints
 *               like `_diffStatus` we stamp later).
 *   - unchanged: the rest
 *
 * We also diff edges keyed by `${source}__${sourceHandle||'default'}__${target}`.
 * Two edges with the same key are considered "the same edge" — otherwise
 * any handle/endpoint change surfaces as an addition + removal, which
 * is what you want to see as a reviewer.
 *
 * The companion `buildUnionPayload` merges the two payloads into a single
 * `{nodes, connections}` that the existing layout pipeline can lay out
 * without any changes — removed-from-current nodes are brought back in
 * so the reviewer can see where they used to sit; each node carries a
 * `_diffStatus` hint on `config` so the renderer can tint it.
 *
 * Keeping the diff as a pure module makes it trivial to unit-test the
 * ranking of each status bucket (see diffPayload.test.js).
 */

const EMPTY_ARR = Object.freeze([]);

/**
 * Canonicalise a node's relevant fields for modification detection.
 * We strip `_diffStatus` (our own marker, stamped after the diff runs)
 * and sort config keys so key-ordering noise doesn't show up as a diff.
 */
function canonicaliseNodeForCompare(node) {
  if (!node || typeof node !== 'object') return '';
  const label = typeof node.label === 'string' ? node.label : '';
  const type = typeof node.type === 'string' ? node.type : '';
  const cfg = node.config && typeof node.config === 'object' ? node.config : {};
  const stripped = {};
  const keys = Object.keys(cfg).sort();
  for (const k of keys) {
    if (k === '_diffStatus') continue;
    stripped[k] = cfg[k];
  }
  return JSON.stringify({ label, type, config: stripped });
}

function edgeKey(e) {
  const source = e.source != null ? e.source : e.from;
  const target = e.target != null ? e.target : e.to;
  const handle = e.sourceHandle != null ? e.sourceHandle : (e.fromPort != null ? e.fromPort : 'default');
  return `${source}__${handle}__${target}`;
}

function indexById(nodes) {
  const m = new Map();
  for (const n of nodes || EMPTY_ARR) {
    if (n && n.id != null) m.set(n.id, n);
  }
  return m;
}

function indexByEdgeKey(edges) {
  const m = new Map();
  for (const e of edges || EMPTY_ARR) {
    if (!e) continue;
    m.set(edgeKey(e), e);
  }
  return m;
}

/**
 * Compute the structural diff.
 *
 * @param {{nodes: object[], connections: object[]}} base
 * @param {{nodes: object[], connections: object[]}} current
 * @returns {{
 *   nodes: { added: Set<string>, removed: Set<string>, modified: Set<string>, unchanged: Set<string> },
 *   edges: { added: Set<string>, removed: Set<string>, unchanged: Set<string> },
 *   counts: { added: number, removed: number, modified: number }
 * }}
 */
export function computeDiff(base, current) {
  const baseNodes = indexById(base && base.nodes);
  const curNodes = indexById(current && current.nodes);

  const added = new Set();
  const removed = new Set();
  const modified = new Set();
  const unchanged = new Set();

  for (const id of curNodes.keys()) {
    if (!baseNodes.has(id)) { added.add(id); continue; }
    const a = canonicaliseNodeForCompare(baseNodes.get(id));
    const b = canonicaliseNodeForCompare(curNodes.get(id));
    if (a !== b) modified.add(id);
    else unchanged.add(id);
  }
  for (const id of baseNodes.keys()) {
    if (!curNodes.has(id)) removed.add(id);
  }

  const baseEdges = indexByEdgeKey(base && base.connections);
  const curEdges = indexByEdgeKey(current && current.connections);
  const eAdded = new Set();
  const eRemoved = new Set();
  const eUnchanged = new Set();
  for (const k of curEdges.keys()) {
    if (baseEdges.has(k)) eUnchanged.add(k);
    else eAdded.add(k);
  }
  for (const k of baseEdges.keys()) {
    if (!curEdges.has(k)) eRemoved.add(k);
  }

  return {
    nodes: { added, removed, modified, unchanged },
    edges: { added: eAdded, removed: eRemoved, unchanged: eUnchanged },
    counts: {
      added: added.size,
      removed: removed.size,
      modified: modified.size,
    },
  };
}

/**
 * Build the union payload — the `current` payload with REMOVED nodes
 * from base merged back in so the reviewer can see where they used to
 * live. Each node gets `config._diffStatus` stamped so the renderer
 * layer can tint it. Edges removed-but-not-in-current come back too,
 * carrying `_diffStatus` on their label-less copy.
 *
 * Why merge into current instead of showing two canvases side-by-side:
 * a single canvas lets the reviewer see the DAG shape as a whole, with
 * additions and removals in context. Side-by-side makes you scan with
 * your eyes; in-line shows you what was altered at a glance. Conductor OSS has
 * neither; this is the differentiator.
 *
 * @param {object} base
 * @param {object} current
 * @param {ReturnType<typeof computeDiff>} diff
 * @returns {object}
 */
export function buildUnionPayload(base, current, diff) {
  if (!current || !Array.isArray(current.nodes)) return current;
  const baseNodes = indexById(base && base.nodes);
  const baseEdges = indexByEdgeKey(base && base.connections);

  const stampedNodes = current.nodes.map((n) => {
    let status = 'unchanged';
    if (diff.nodes.added.has(n.id)) status = 'added';
    else if (diff.nodes.modified.has(n.id)) status = 'modified';
    return {
      ...n,
      config: { ...(n.config || {}), _diffStatus: status },
    };
  });

  // Ghost-add the removed-from-current nodes so the reviewer can see
  // where they were. They come from the base's node list with a
  // 'removed' stamp. We DON'T deep-clone config — the status stamp is
  // enough to style them.
  for (const id of diff.nodes.removed) {
    const removedNode = baseNodes.get(id);
    if (!removedNode) continue;
    stampedNodes.push({
      ...removedNode,
      config: { ...(removedNode.config || {}), _diffStatus: 'removed' },
    });
  }

  const stampedEdges = (current.connections || []).map((e) => ({
    ...e,
    _diffStatus: diff.edges.added.has(edgeKey(e)) ? 'added' : 'unchanged',
  }));
  for (const k of diff.edges.removed) {
    const removedEdge = baseEdges.get(k);
    if (!removedEdge) continue;
    stampedEdges.push({ ...removedEdge, _diffStatus: 'removed' });
  }

  return {
    ...current,
    nodes: stampedNodes,
    connections: stampedEdges,
  };
}
